/**
 * Loot no chão: equipamento dropado com beam/aura por raridade. Magnetiza em curta
 * distância e faz AUTO-PICKUP dentro de pickupRadius. Se a bolsa estiver cheia, o item
 * PERMANECE no chão (não some silenciosamente). Meshes reciclados (pool).
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { Pool } from "../utils/Pool";
import { RARITY_COLOR } from "../items/Rarity";
import type { EquipmentInstance } from "../items/EquipmentTypes";

interface Drop {
  inst: EquipmentInstance;
  group: THREE.Group;
  gem: THREE.Mesh;
  beam: THREE.Mesh;
  gemMat: THREE.MeshStandardMaterial;
  beamMat: THREE.MeshBasicMaterial;
  bob: number;
}

export class GroundLoot {
  private readonly active: Drop[] = [];
  private readonly pool: Pool<Drop>;
  private readonly cfg = GameConfig.loot;

  constructor(private readonly scene: THREE.Scene) {
    this.pool = new Pool<Drop>(() => this.make(), (d) => (d.group.visible = false), 6);
  }

  drop(inst: EquipmentInstance, x: number, z: number): void {
    const d = this.pool.acquire();
    const color = RARITY_COLOR[inst.rarity];
    d.inst = inst;
    d.gemMat.color.setHex(color);
    d.gemMat.emissive.setHex(color);
    d.beamMat.color.setHex(color);
    // beam maior conforme raridade (feedback discreto no mobile)
    const idx = GameConfig.rarity.order.indexOf(inst.rarity);
    d.beam.scale.set(1, 1 + idx * 0.5, 1);
    d.beamMat.opacity = 0.12 + idx * 0.04;
    d.group.position.set(x, 0, z);
    d.bob = Math.random() * Math.PI * 2;
    d.group.visible = true;
    this.active.push(d);
  }

  /**
   * Magnetiza e coleta; `tryPickup` retorna false se a bolsa estiver cheia.
   * `pullBlocked(x,z)` (opcional): se true p/ um item, o magnetismo/coleta ficam
   * desativados (o loot não atravessa a barreira da Sanctuary).
   */
  update(
    dt: number,
    playerPos: THREE.Vector3,
    tryPickup: (inst: EquipmentInstance) => boolean,
    pullBlocked?: (x: number, z: number) => boolean,
  ): void {
    const mag = this.cfg.magnetRadius;
    const pick = this.cfg.pickupRadius;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const d = this.active[i];
      d.bob += dt * 3;
      d.gem.rotation.y += dt * 1.5;
      d.gem.position.y = 0.7 + Math.sin(d.bob) * 0.15;

      if (pullBlocked?.(d.group.position.x, d.group.position.z)) continue; // não atravessa a barreira

      const dx = playerPos.x - d.group.position.x;
      const dz = playerPos.z - d.group.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist < mag && dist > pick) {
        const s = (this.cfg.magnetRadius * 3 * dt) / Math.max(dist, 0.001);
        d.group.position.x += dx * s;
        d.group.position.z += dz * s;
      }
      if (dist <= pick) {
        if (tryPickup(d.inst)) {
          this.pool.release(d);
          this.active.splice(i, 1);
        }
      }
    }
  }

  clear(): void {
    for (const d of this.active) this.pool.release(d);
    this.active.length = 0;
  }

  private make(): Drop {
    const group = new THREE.Group();
    const gemMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.8, flatShading: true });
    const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.35, 0), gemMat);
    gem.position.y = 0.7;
    const beamMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.2, depthWrite: false, side: THREE.DoubleSide });
    const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 3, 6, 1, true), beamMat);
    beam.position.y = 1.5;
    group.add(gem, beam);
    group.visible = false;
    this.scene.add(group);
    return { inst: null as unknown as EquipmentInstance, group, gem, beam, gemMat, beamMat, bob: 0 };
  }
}
